import { useState, useEffect } from "react";
import { Edit2, Trash2, Cpu, Users, Plus, Building2 } from "lucide-react";
import { SearchBar, EmptyState } from "../../index.jsx";
import Pagination from "../../../common/Pagination.jsx";
import ConfirmDeleteModal from "../users/ConfirmDeleteModal.jsx";

export default function MappingsTable({
  mappings = [],
  onEdit,
  onDelete,
  onToggleStatus,
  getUserName = () => "Unknown User",
  getUserEmail = () => "",
  getModelGroupName = () => "Unknown Group",
  getCompanyName = () => "Unknown Company",
  onAddNew,
  searchQuery = "",
  setSearchQuery = () => {},
}) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const filtered = mappings.filter(m => {
    const q = (searchQuery || "").toLowerCase();
    return (
      (getUserName(m?.userId) || "").toLowerCase().includes(q) ||
      (getModelGroupName(m?.modelGroupId) || "").toLowerCase().includes(q) ||
      (getCompanyName(m?.companyId) || "").toLowerCase().includes(q)
    );
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated  = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => { setCurrentPage(1); }, [searchQuery, itemsPerPage]);

  const handleDeleteClick = (m) => { setSelectedMapping(m); setShowDeleteModal(true); };
  const handleConfirmDelete = () => {
    if (selectedMapping) { onDelete(selectedMapping); setShowDeleteModal(false); setSelectedMapping(null); }
  };

  if (!mappings.length) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-[2rem] shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden">
        <div className="p-5 border-b border-slate-100 dark:border-slate-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-white ml-2">User Model Group Mappings</h3>
          <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Search…" />
        </div>
        <div className="text-center py-14">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-700 mb-4">
            <Users size={28} className="text-slate-400" />
          </div>
          <h3 className="text-base font-semibold text-slate-800 dark:text-white mb-1">No assignments yet</h3>
          <p className="text-sm text-slate-500 mb-5">Create your first user model group assignment.</p>
          <button
            onClick={onAddNew}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-[#1d57a5] hover:bg-[#164280] text-white rounded-full transition-all shadow-sm"
          >
            <Plus size={15} /> New Assignment
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-[2rem] shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden flex flex-col max-h-[calc(100vh-140px)]">
      {/* Header */}
      <div className="p-5 border-b border-slate-100 dark:border-slate-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4 flex-shrink-0">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-white ml-2">User Model Group Mappings</h3>
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
          />
          <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Search by user, company or group…" />
          <button
            onClick={onAddNew}
            className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-[#1d57a5] hover:bg-[#164280] text-white rounded-full transition-all shadow-sm"
          >
            <Plus size={15} /> Create Assignment
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto hide-scrollbar">
        <table className="w-full text-left border-collapse min-w-[750px]">
          <thead>
            <tr className="border-b border-slate-100 dark:border-slate-700 text-slate-500 dark:text-slate-400 text-sm">
              <th className="px-6 py-4 font-medium whitespace-nowrap">Company</th>
              <th className="px-6 py-4 font-medium whitespace-nowrap">User</th>
              <th className="px-6 py-4 font-medium whitespace-nowrap">Model Group</th>
              <th className="px-6 py-4 font-medium whitespace-nowrap">Status</th>
              <th className="px-6 py-4 font-medium text-center whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
            {paginated.length > 0 ? paginated.map(m => (
              <tr key={m?.id ?? Math.random()} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition-colors group">
                {/* Company */}
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <Building2 size={14} className="text-slate-400 flex-shrink-0" />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">
                      {getCompanyName(m?.companyId)}
                    </span>
                  </div>
                </td>
                {/* User */}
                <td className="px-6 py-4">
                  <div className="font-semibold text-slate-800 dark:text-slate-200 whitespace-nowrap group-hover:text-[#1d57a5] transition-colors">
                    {getUserName(m?.userId)}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 whitespace-nowrap">{getUserEmail(m?.userId)}</div>
                </td>
                {/* Model Group */}
                <td className="px-6 py-4">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-xs font-medium">
                    <Cpu size={12} /> {getModelGroupName(m?.modelGroupId)}
                  </span>
                </td>
                {/* Status */}
                <td className="px-6 py-4">
                  <button
                    onClick={() => onToggleStatus(m)}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold tracking-wide transition-colors ${
                      m?.isActive
                        ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-200"
                        : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 hover:bg-red-200"
                    }`}
                  >
                    {m?.isActive ? "ACTIVE" : "INACTIVE"}
                  </button>
                </td>
                {/* Actions */}
                <td className="px-6 py-4 text-center">
                  <div className="flex items-center justify-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-xl p-1 shadow-sm">
                    <button
                      onClick={() => onEdit(m)}
                      className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit2 size={15} />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(m)}
                      className="p-2 text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            )) : (
              <tr><td colSpan={5} className="py-12 text-center"><EmptyState query={searchQuery} label="mappings" /></td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedMapping && (
        <ConfirmDeleteModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleConfirmDelete}
          mappingName={`${getUserName(selectedMapping?.userId)} — ${getModelGroupName(selectedMapping?.modelGroupId)}`}
        />
      )}
    </div>
  );
}
